import { ElementRef, Injectable, inject, DOCUMENT } from '@angular/core';
import { getBoundingClientRect } from '../../utils/dom.utils';
import { CSSVariables, ResponsiveService } from './responsive.service';

@Injectable({
  providedIn: 'root',
})
export class ScrollerService {
  private readonly _responsive: ResponsiveService = inject(ResponsiveService);
  private readonly _document: Document = inject<Document>(DOCUMENT);

  private _navHeight: number = 0;
  private _pushHeight: number = 0;
  private readonly _window?: Window | null;
  private readonly _history?: History;

  public constructor() {
    this._window = this._document.defaultView;
    this._history = this._window?.history;
  }

  public bootstrap(): void {
    this._responsive.variables$.subscribe((variables: CSSVariables) => {
      const computedStyle: CSSStyleDeclaration | undefined =
        this._window?.getComputedStyle?.(this._document.documentElement);
      const rootFontSize: number = Number.parseFloat(
        computedStyle?.fontSize ?? '16'
      );
      // Variables are in rem, convert to pixels
      this._navHeight =
        Number.parseFloat(variables.navHeight ?? '0') * rootFontSize;
      this._pushHeight =
        Number.parseFloat(variables.pushHeight ?? '0') * rootFontSize;
    });
  }

  public set scrollRestoration(value: 'auto' | 'manual') {
    if (this._history) {
      this._history.scrollRestoration = value;
    }
  }

  public scrollToPosition(position: [number, number], smooth?: boolean): void {
    if (this._window?.scroll) {
      this._window.scroll({
        top: position[1],
        left: position[0],
        behavior: smooth ? 'smooth' : 'auto',
      });
    }
  }

  public scrollToAnchor(id: string, smooth?: boolean): void {
    const element: HTMLElement | null = this._document.getElementById(id);
    if (element) {
      this.scrollTo(element, smooth);
    }
  }

  public scrollTo(element: HTMLElement, smooth?: boolean): void {
    const elemY: number = getBoundingClientRect(element).top;
    const windowY: number = this._window?.scrollY ?? 0;

    // header is always visible, so always offset by its height
    const scroll: number = elemY + windowY - this._navHeight - this._pushHeight;

    if (this._window?.scroll) {
      this._window.scroll({
        top: scroll,
        left: 0,
        behavior: smooth ? 'smooth' : 'auto',
      });
    }
  }

  public scrollTop(smooth?: boolean): void {
    if (this._window?.scroll) {
      this._window.scroll({
        top: 0,
        left: 0,
        behavior: smooth ? 'smooth' : 'auto',
      });
    }
  }

  public scrollIntoView(
    elementRef?: ElementRef | HTMLElement,
    smooth: boolean = true,
    block: ScrollLogicalPosition = 'start'
  ): void {
    const element: HTMLElement | undefined =
      elementRef instanceof HTMLElement
        ? elementRef
        : elementRef?.nativeElement;
    if (element) {
      element.scrollIntoView({
        behavior: smooth ? 'smooth' : 'instant',
        block,
      });
    }
  }
}
